import { apiRequest } from "./queryClient";

// Training Modules API
export class TrainingModulesAPI {
  static async getAll() {
    const response = await apiRequest("GET", "/training-modules");
    return response.json();
  }

  static async getById(id: string) {
    const response = await apiRequest("GET", `/training-modules/${id}`);
    return response.json();
  }

  static async create(data: {
    title: string;
    description: string;
    content: string;
    duration: number;
    order: number;
  }) {
    const response = await apiRequest("POST", "/training-modules", data);
    return response.json();
  }

  static async update(id: string, data: Partial<{
    title: string;
    description: string;
    content: string;
    duration: number;
    order: number;
    isActive: boolean;
  }>) {
    const response = await apiRequest("PUT", `/training-modules/${id}`, data);
    return response.json();
  }

  static async delete(id: string) {
    const response = await apiRequest("DELETE", `/training-modules/${id}`);
    return response.json();
  }
}

// Quizzes API
export class QuizzesAPI {
  static async getByModule(moduleId: string) {
    const response = await apiRequest("GET", `/quizzes/module/${moduleId}`);
    return response.json();
  }

  static async getById(id: string) {
    const response = await apiRequest("GET", `/quizzes/${id}`);
    return response.json();
  }

  static async create(data: {
    moduleId: string;
    title: string;
    description: string;
    questions: Array<{
      id: string;
      question: string;
      options: string[];
      correctAnswer: number;
    }>;
    passingScore: number;
  }) {
    const response = await apiRequest("POST", "/quizzes", data);
    return response.json();
  }

  static async update(id: string, data: Partial<{
    title: string;
    description: string;
    questions: Array<{
      id: string;
      question: string;
      options: string[];
      correctAnswer: number;
    }>;
    passingScore: number;
    isActive: boolean;
  }>) {
    const response = await apiRequest("PUT", `/quizzes/${id}`, data);
    return response.json();
  }

  static async delete(id: string) {
    const response = await apiRequest("DELETE", `/quizzes/${id}`);
    return response.json();
  }
}

// Progress API
export class ProgressAPI {
  static async getUserProgress() {
    const response = await apiRequest("GET", "/progress");
    return response.json();
  }

  static async updateProgress(data: {
    moduleId: string;
    isCompleted: boolean;
    timeSpent?: number;
    completedAt?: string;
  }) {
    const response = await apiRequest("POST", "/progress", data);
    return response.json();
  }

  static async getProgressStats() {
    const progress = await this.getUserProgress();
    const completedModules = progress.progress?.filter((p: any) => p.isCompleted)?.length || 0;
    const totalTimeSpent = progress.progress?.reduce((acc: number, p: any) => acc + (p.timeSpent || 0), 0) || 0;
    
    return {
      completedModules,
      totalTimeSpent,
      progressPercentage: 0, // This would be calculated with total modules
    };
  }
}

// Quiz Attempts API
export class QuizAttemptsAPI {
  static async getUserAttempts() {
    const response = await apiRequest("GET", "/quiz-attempts");
    return response.json();
  }

  static async submitAttempt(data: {
    quizId: string;
    answers: Record<string, number>;
    score: number;
    isPassed: boolean;
  }) {
    const response = await apiRequest("POST", "/quiz-attempts", data);
    return response.json();
  }

  static async getAttemptsByQuiz(quizId: string) {
    const attempts = await this.getUserAttempts();
    return attempts.attempts?.filter((attempt: any) => attempt.quizId === quizId) || [];
  }

  static async getAverageScore() {
    const attempts = await this.getUserAttempts();
    if (!attempts.attempts || attempts.attempts.length === 0) return 0;
    
    const totalScore = attempts.attempts.reduce((acc: number, attempt: any) => acc + attempt.score, 0);
    return Math.round(totalScore / attempts.attempts.length);
  }
}

// Feedback API
export class FeedbackAPI {
  static async getAll() {
    const response = await apiRequest("GET", "/feedback");
    return response.json();
  }

  static async submit(data: {
    rating: number;
    comments: string;
    category: string;
  }) {
    const response = await apiRequest("POST", "/feedback", data);
    return response.json();
  }

  static async getFeedbackStats() {
    const feedback = await this.getAll();
    if (!feedback.feedback || feedback.feedback.length === 0) {
      return {
        totalFeedback: 0,
        averageRating: 0,
        categories: [],
      };
    }

    const totalFeedback = feedback.feedback.length;
    const averageRating = feedback.feedback.reduce((acc: number, item: any) => acc + item.rating, 0) / totalFeedback;
    const categories = Array.from(new Set(feedback.feedback.map((item: any) => item.category)));

    return {
      totalFeedback,
      averageRating: Math.round(averageRating * 10) / 10,
      categories,
    };
  }
}



// Users API (Admin only)
export class UsersAPI {
  static async getAll() {
    const response = await apiRequest("GET", "/admin/users");
    return response.json();
  }

  static async getById(id: string) {
    const response = await apiRequest("GET", `/admin/users/${id}`);
    return response.json();
  }

  static async update(id: string, data: Partial<{
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  }>) {
    const response = await apiRequest("PUT", `/admin/users/${id}`, data);
    return response.json();
  }

  static async delete(id: string) {
    const response = await apiRequest("DELETE", `/admin/users/${id}`);
    return response.json();
  }
}

// Analytics API (Admin only)
export class AnalyticsAPI {
  static async getDashboardStats() {
    // This would combine data from multiple endpoints
    const [modules, feedback, progress] = await Promise.all([
      TrainingModulesAPI.getAll().catch(() => ({ modules: [] })),
      FeedbackAPI.getAll().catch(() => ({ feedback: [] })),
      ProgressAPI.getUserProgress().catch(() => ({ progress: [] })),
    ]);

    return {
      totalModules: modules.modules?.length || 0,
      totalFeedback: feedback.feedback?.length || 0,
      averageCompletion: 78, // This would be calculated from actual data
      activeUsers: 45, // This would come from user activity data
    };
  }

  static async getProgressReport() {
    // This would provide detailed progress analytics
    return {
      moduleCompletionRates: [],
      userProgressSummary: [],
      quizPerformance: [],
    };
  }

  static async getFeedbackReport() {
    const feedback = await FeedbackAPI.getAll();
    
    return {
      feedbackTrends: [],
      categoryBreakdown: {},
      sentimentAnalysis: {},
      rawFeedback: feedback.feedback || [],
    };
  }
}

// Utility functions for API error handling
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export function handleAPIError(error: any): APIError {
  if (error instanceof APIError) {
    return error;
  }

  // Parse error message from API response
  const message = error.message || 'An unexpected error occurred';
  const status = error.status || 500;
  
  return new APIError(message, status, error);
}

// Utility for creating toast messages from API responses
export function getToastFromAPIResponse(response: any, defaultMessage: string = 'Operation completed') {
  return {
    title: response.success ? 'Success' : 'Error',
    description: response.message || defaultMessage,
    variant: response.success ? 'default' : 'destructive' as const,
  };
}

// Constants for API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    ME: '/auth/me',
  },
  TRAINING_MODULES: '/training-modules',
  QUIZZES: '/quizzes',
  PROGRESS: '/progress',
  QUIZ_ATTEMPTS: '/quiz-attempts',
  FEEDBACK: '/feedback',

  USERS: '/admin/users',
} as const;
